import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GetEmployeesResponse, substitutionsApi, UserInfo } from 'app/services/edpApiV1/substitutions';
import { RootState } from 'app/store';
import { ComponentModes, SubstitutionsTypes } from 'shared/enums';
import { DivisionsTypes, SortDivision } from 'shared/enums/substitutions';
import { Pagination } from 'shared/types/types';

type TDrawerInfo = {
  mode: ComponentModes;
  substitution: { type: SubstitutionsTypes; id?: string };
  orgId?: string;
  isOpen: boolean;
};

type TSubstitutionsUI = {
  controlDrawer: TDrawerInfo | null;
  selectedEymployerId?: null | string;
  searchEmployeesOptions: UserInfo[];
  pagination: Omit<Pagination, 'total'>;
  filter?: DivisionsTypes;
  search?: { personnelNumber?: string; search?: string; divisionSearch?: string };
  order?: {
    sortByDivision?: SortDivision.divisionAsc | SortDivision.divisionDesc;
    sortBySupervisor?: SortDivision.supervisorAsc | SortDivision.supervisorDesc;
  };
  buttonDisabled: boolean;
};

const initialState: TSubstitutionsUI = {
  controlDrawer: null,
  selectedEymployerId: null,
  searchEmployeesOptions: [],
  pagination: {
    page: 1,
    size: 10,
  },
  buttonDisabled: false,
};

export const SubstitutionsSlice = createSlice({
  name: 'ui/substitutions',
  initialState,
  extraReducers: builder => {
    builder
      .addMatcher(
        substitutionsApi.endpoints.getEmployeesForSubstitution.matchFulfilled,
        (state, { payload }: PayloadAction<GetEmployeesResponse>) => {
          return {
            ...state,
            searchEmployeesOptions:
              payload.pagination.page === 1 ? payload.data : [...state.searchEmployeesOptions, ...payload.data],
          };
        },
      )
      .addMatcher(substitutionsApi.endpoints.getAllDivisions.matchRejected, state => {
        return { ...state, searchEmployeesOptions: [...state.searchEmployeesOptions] };
      });
  },
  reducers: {
    // Drawer
    openSubstitutionControlDrawer: (state, { payload: drawerInfo }: PayloadAction<Omit<TDrawerInfo, 'isOpen'>>) => {
      return {
        ...state,
        controlDrawer: { ...drawerInfo, isOpen: true },
        buttonDisabled: drawerInfo.mode === ComponentModes.edit,
      };
    },
    closeSubstitutionControlDrawer: state => {
      return { ...state, controlDrawer: null, selectedEymployerId: null, searchEmployeesOptions: [] };
    },
    setSelectedEymployerId: (state, { payload }: PayloadAction<string | undefined>) => {
      return {
        ...state,
        selectedEymployerId: payload,
        searchEmployeesOptions: payload ? [state.searchEmployeesOptions.find(item => item?.id === payload)!] : [],
      };
    },
    setEymployeesOptions: (state, { payload }: PayloadAction<UserInfo[]>) => {
      return { ...state, searchEmployeesOptions: payload };
    },
    setButtonDisabled: (state, { payload }: PayloadAction<boolean>) => {
      return { ...state, buttonDisabled: payload };
    },
    // Поиск, сортировка, фильтрация
    setFilter: (state, { payload }: PayloadAction<DivisionsTypes | undefined>) => {
      return { ...state, filter: payload, pagination: { ...state.pagination, page: 1 } };
    },
    setSearch: (state, { payload }: PayloadAction<{ personnelNumber?: string; search?: string }>) => {
      return { ...state, search: payload, pagination: { ...state.pagination, page: 1 } };
    },
    resetSearch: state => {
      return { ...state, search: initialState.search };
    },
    setSortByDivision: (
      state,
      { payload }: PayloadAction<SortDivision.divisionAsc | SortDivision.divisionDesc | undefined>,
    ) => {
      return {
        ...state,
        order: { sortByDivision: payload },
      };
    },
    setSortBySupervisor: (
      state,
      { payload }: PayloadAction<SortDivision.supervisorAsc | SortDivision.supervisorDesc | undefined>,
    ) => {
      return {
        ...state,
        order: { sortBySupervisor: payload },
      };
    },
    // Пагинация
    setPagination: (state, { payload }: PayloadAction<Omit<Pagination, 'total'>>) => {
      return { ...state, pagination: payload };
    },
    setPage: (state, { payload }: PayloadAction<number>) => {
      return { ...state, pagination: { ...state.pagination, page: payload } };
    },
    setSize: (state, { payload }: PayloadAction<number>) => {
      return { ...state, pagination: { ...state.pagination, size: payload } };
    },
    resetSubstitutionsUiState: () => initialState,
  },
});

const selectSubstitutions = (state: RootState) => state.ui.substitutions;
const getPagination = createSelector(selectSubstitutions, state => state.pagination);
const getSearchEymployeesOptions = createSelector(selectSubstitutions, state => state.searchEmployeesOptions);
const getSearchParams = createSelector(selectSubstitutions, state => {
  return { filter: state.filter, ...state.search, order: Object.values(state.order ? state.order : {}) };
});

export const selectors = { selectSubstitutions, getPagination, getSearchParams, getSearchEymployeesOptions };
export const actions = SubstitutionsSlice.actions;
export const reducer = SubstitutionsSlice.reducer;
